// LLM generated - Model Claude 4 Sonnet
import { Stack, Group, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';
import { Avatar } from '~/domains/design-system/Avatar';

import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { useChatWebFeatureFlags } from '~/domains/common/hooks/useChatWebFeatureFlags';

import type { Notification } from '../notifications.types';
import { NotificationType } from '../notifications.types';
import { useNotificationData } from '../hooks/useNotificationData';
import { useStyles } from './NotificationInfoModal.styles';

type CommissionerPostMessageContentProps = {
  notification: Notification;
};

export function CommissionerPostMessageContent({
  notification,
}: CommissionerPostMessageContentProps) {
  const { t } = useTranslation('notifications');
  const theme = useMantineTheme();
  const { classes } = useStyles();

  // Check if posts are available on web (feature flag + treatment)
  const { isChatGloballyEnabled, isPostsWebEnabled } = useChatWebFeatureFlags();
  const canViewPostsOnWeb = isChatGloballyEnabled && isPostsWebEnabled;

  // Extract all notification data using consolidated hook (must be called before any early returns)
  const {
    author: { handle: authorHandle, avatarUrl: authorAvatar },
    payload: { header: postHeader, message: postMessage },
    timestamp: { formatted: timestamp },
  } = useNotificationData(notification);

  // Graceful fallback for unsupported notification types
  if (notification.type !== NotificationType.CONTEST_POST_MESSAGE_PUBLISHED) {
    return (
      <Text variant="body-large" c={theme.colors.gray[6]}>
        {t('notifications.modals.commissionerPost.unsupportedNotificationType')}
      </Text>
    );
  }

  return (
    <Stack spacing={theme.other.spacing._16}>
      {/* Author Header */}
      <Group spacing={theme.other.spacing._12} align="flex-start">
        <Avatar handle={authorHandle} src={authorAvatar} size="md" />
        <Stack spacing={theme.other.spacing._4} className={classes.flexContainer}>
          <Text variant="body-large" fw={600}>
            @{authorHandle}
          </Text>
          <Text variant="body-small" c={theme.colors.gray[6]}>
            {t('notifications.modals.commissionerPost.content.role')}
          </Text>
        </Stack>
      </Group>

      {/* Timestamp */}
      {timestamp && (
        <Group spacing={theme.other.spacing._4}>
          <Text variant="title-small">
            {t('notifications.modals.commissionerPost.content.timestamp')}
          </Text>
          <Text variant="body-small" c={theme.colors.gray[5]}>
            {timestamp}
          </Text>
        </Group>
      )}

      {/* Title and Message Content with tighter spacing */}
      <Stack spacing={theme.other.spacing._8}>
        <Text variant="headline-xl">{postHeader}</Text>
        <Text
          variant="body-large"
          className={classes.noFirstParagraphMargin}
          dangerouslySetInnerHTML={{
            __html: toSafeMarkup(postMessage, { useMarkdown: true }),
          }}
        />
      </Stack>

      {!canViewPostsOnWeb && (
        <Text variant="body-medium" c={theme.colors.gray2[3]}>
          {t('notifications.modals.commissionerPost.content.appDownloadPrompt')}
        </Text>
      )}
    </Stack>
  );
}
